import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { isDateAfter } from 'odfjell-date-time-utils/dateTimeUtils';
import { StowagePlanShareTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanShareTypeEnum';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { getCurrentPortCall } from './stowageRotationUtils';
import { UUID } from 'crypto';

const sortByLastUpdated = (a: StowagePlan, b: StowagePlan): number => {
    return isDateAfter(b.lastUpdatedUtc, a.lastUpdatedUtc) ? 1 : -1;
};

export const sortStowagePlans = (stowagePlans: StowagePlan[]): StowagePlan[] => {
    return stowagePlans.sort(sortByLastUpdated);
};

export const getMasterStowagePlan = (stowagePlans: StowagePlan[]): StowagePlan | undefined => {
    return stowagePlans.find((s) => s.type === StowagePlanTypeEnum.Master && !s.isDeleted);
};

export const getNonMastertowagePlans = (stowagePlans: StowagePlan[], currentUserName?: string): StowagePlan[] => {
    return stowagePlans
        .filter((s) => {
            if (s.type === StowagePlanTypeEnum.Master || s.isDeleted) {
                return false;
            }
            // Filter out draft plans that don't belong to the current user
            if (s.type === StowagePlanTypeEnum.Draft && currentUserName && s.owner !== currentUserName) {
                return false;
            }
            return true;
        })
        .sort(sortByLastUpdated);
};

export const getStowagePlanType = (stowagePlan: StowagePlan) => {
    return stowagePlan.type === StowagePlanTypeEnum.Master
        ? 'master'
        : stowagePlan.type === StowagePlanTypeEnum.Published
        ? 'published'
        : stowagePlan.shareType === StowagePlanShareTypeEnum.Shared
        ? 'shared'
        : 'draft';
};

export const getStowagePlanTypeTitle = (stowagePlanType: string) => {
    const titles = {
        master: 'Master plan',
        published: 'Published plan',
        shared: 'Shared plan',
        draft: 'Draft plan',
    };

    return titles[stowagePlanType];
};

export const createStowagePlanName = (stowagePlan: StowagePlan, vessel: Vessel): string => {
    const currentPortCall = getCurrentPortCall(stowagePlan);
    const voyageNumber = currentPortCall?.voyageNumber;
    return voyageNumber ? `${vessel.vesselName} ${voyageNumber}` : vessel.vesselName;
};

/**
 * Populate the master plan's syncedWithImosDateTimeUtc from its parent chain if it's missing.
 * Traverses upwards following parentGuid references and picks the latest (most recent) non-null date.
 * Does nothing if master plan already has a syncedWithImosDateTimeUtc value.
 */
export const fillMissingImosSyncDateFromParents = (masterPlan: StowagePlan | undefined, stowagePlans: StowagePlan[]): void => {
    if (!masterPlan || masterPlan.syncedWithImosDateTimeUtc) return;
    if (!masterPlan.parentGuid) return;

    const planMap = new Map<UUID, StowagePlan>(stowagePlans.map((p) => [p.stowagePlanGuid, p]));
    const visited = new Set<UUID>();
    let current = planMap.get(masterPlan.parentGuid);
    let latest: Date | null = null;
    while (current && !visited.has(current.stowagePlanGuid)) {
        visited.add(current.stowagePlanGuid);
        if (current.syncedWithImosDateTimeUtc) {
            if (!latest || isDateAfter(current.syncedWithImosDateTimeUtc as any, latest as any)) {
                latest = current.syncedWithImosDateTimeUtc as any;
            }
        }
        current = current.parentGuid ? planMap.get(current.parentGuid) : null;
    }
    if (latest) {
        masterPlan.syncedWithImosDateTimeUtc = latest as any;
    }
};
